import {
  AvailabilityBySourceItem,
  PartList
} from 'services/availabilityBySource/types';
import { isEmpty } from '@cat-ecom/common/utils/commonUtils';
import { getSetCookieValue } from 'utils/common';
import { LineItemNotes, OrderItemsResponse, PartsErrors } from './types';
import {
  SOSItem,
  SOSListItemHeadless,
  SourceOfSupplyPartsList,
  SourceOfSupplyResponse,
  SourceOfSupplyResponseHeadless
} from 'services/source-of-supply/types';
import { OrderItemsAddPartToSavedList } from 'services/savedLists/types';
import { PART_TYPE } from './constants';
import {
  Price,
  PriceAvailabilityLegacyItem,
  PriceAvailabilityLegacyResponse
} from 'services/priceAvailabilityLegacy/types';
import { OrderItem } from '@cat-ecom/common/utils/productFormUtils';
import { QuickOrderCookieData } from 'app/[lang]/[esite]/(core)/cart/_components/quick-order-section/quick-order-form-wrapper/quick-order-form-wrapper';
import { PartListWithSOS, QOItem } from 'services/order/types';
import {
  AvailabilityHeadless,
  Part,
  PartLevelFlags,
  PriceAvailabilityResponseHeadless,
  ProductPriceAvailabilityHeadless,
  ReplacementPartsHeadless
} from 'services/priceAvailability/types';
import { PartType } from 'app/[lang]/[esite]/(core)/cart/constants/common';

export const getPartsList = (
  values,
  modify = false,
  isIncludeRefId = false
): PartList[] => {
  return values
    ? values
        .filter((val) => val.itemNumber !== '')
        .map((val) => {
          return {
            partNumber:
              val.itemNumber?.toUpperCase() ?? val.partNumber?.toUpperCase(),
            quantity: modify ? 1 : parseInt(val.quantity),
            sosCode: val.sosCode,
            ...(isIncludeRefId
              ? {
                  referenceId: val?.referenceId || val?.parentRefId
                }
              : {})
          };
        })
    : [];
};

export const getPartsListForSOS = (
  values: OrderItem[]
): SourceOfSupplyPartsList[] =>
  values
    .filter((val) => val.itemNumber !== '')
    .map((val) => ({
      partNumber:
        val.itemNumber?.toUpperCase() ?? val.partNumber?.toUpperCase(),
      quantity: val.quantity,
      referenceId: val.referenceId
    }));

export function getTotalQuantity(partsList): number {
  const totalQuantity: number = partsList
    .map((item) => parseInt(item.quantity))
    .reduce((sum, current) => sum + current);

  return totalQuantity;
}

export const getPartsListSOS = (items) => {
  return items
    ? items.map((item) => {
        return {
          partNumber: item.partNumber,
          sosPartNumber: item.sosList[0].sosPartNumber,
          partName: item.sosList[0].sosPartName,
          sosCode: item.sosList[0].sosCode
        };
      })
    : [];
};

export const getPartIndex = (
  parts,
  partNumber: string,
  currentIndex: number
): number => {
  const partIndexes = parts.reduce((acc, item, index) => {
    if (item.partNumber === partNumber) acc.push(index);
    return acc;
  }, []);

  return partIndexes.indexOf(currentIndex);
};

export const getLineItemNote = (
  formField,
  item: PriceAvailabilityLegacyItem,
  isReplacementPart: boolean
): string => {
  if (isReplacementPart) {
    return `${formField?.lineItemNote ? formField?.lineItemNote + '/' : ''}Replaces Part # (${item.originalPart})`;
  }

  return formField?.lineItemNote ?? '';
};

export const getPartsWithChangesToAdd = (
  parts: PriceAvailabilityLegacyItem[],
  formFields: OrderItem[],
  partType: PartType
) =>
  parts?.map((item) => {
    const isReplacementPart = partType === PartType.REPLACEMENT;
    const isAlternative = partType === PartType.ALTERNATIVE;
    const filteredFormFields = formFields?.filter((fi) => fi.itemNumber !== '');
    const formField = filteredFormFields?.find((f) => {
      if (isAlternative || isReplacementPart) {
        return (
          normalizedPartNumber(f?.itemNumber) ===
            normalizedPartNumber(item?.originalPart) &&
          (f?.referenceId === item?.referenceId ||
            f?.referenceId === item?.parentRefId)
        );
      } else {
        return f?.referenceId === item?.referenceId;
      }
    });

    return {
      partNumber: item.partNumber,
      partName: item.partName,
      quantity: item.quantity,
      sosCode: item.sosCode,
      customerSerialNumber: formField?.serialNumber ?? '',
      lineItemNote: getLineItemNote(formField, item, isReplacementPart),
      customerPartNumber: formField?.customerPartNumber ?? '',
      customerItemNumber: formField?.customerItemNumber ?? '',
      replacedPartNumber: isReplacementPart ? item.originalPart : '',
      isCatPart: item.partLevelFlags?.isCatPart,
      referenceId:
        item?.referenceId || item?.parentRefId || formField?.referenceId
    };
  });

export const getPartsErrors = (
  parts: AvailabilityBySourceItem[] | SOSItem[]
): PartsErrors =>
  parts?.reduce((acc, item) => {
    if (item.sosList?.[0].errors.errorMessage) {
      return {
        ...acc,
        [item.partNumber]: {
          errorCode: item.sosList[0].errors.errorCode,
          quantity: item.requestedQuantity,
          errorMessage: item.sosList[0].errors.errorMessage
        }
      };
    }
    return acc;
  }, {});

export const normalizedPartNumber = (partNumber) => {
  return partNumber?.replace(/-/g, '')?.toUpperCase();
};

export const getPartsWithSOSCode = (
  quickOrderItems: OrderItem[],
  allPartsWithSOSCode: SOSItem[],
  sosDrawerParts?: PartListWithSOS[]
) => {
  const filteredQOItems = quickOrderItems?.filter((fi) => fi.itemNumber !== '');

  return allPartsWithSOSCode?.map((part) => {
    const { requestedQuantity, sosList, referenceId } = part;
    const { sosCode, sosPartName, sosPartNumber } = sosList[0];
    const foundSelectedSosItem =
      sosList?.length > 0 &&
      sosDrawerParts?.find((f) => f.referenceId === referenceId);

    const qoItem = filteredQOItems?.find((f) => f.referenceId === referenceId);

    return {
      partNumber: foundSelectedSosItem
        ? foundSelectedSosItem?.sosPartNumber
        : sosPartNumber,
      partName: foundSelectedSosItem
        ? foundSelectedSosItem?.partName
        : sosPartName,
      referenceId: foundSelectedSosItem
        ? foundSelectedSosItem?.referenceId
        : referenceId,
      quantity: parseInt(requestedQuantity),
      sosCode: foundSelectedSosItem ? foundSelectedSosItem?.sosCode : sosCode,
      customerSerialNumber: qoItem?.serialNumber ?? '',
      lineItemNote: qoItem?.lineItemNote ?? '',
      customerPartNumber: qoItem?.customerPartNumber ?? '',
      customerItemNumber: qoItem?.customerItemNumber ?? '',
      // replacedPartNumber will be added as part of replacement drawer changes
      replacedPartNumber: '',
      isCatPart: true // default
    } as QOItem;
  });
};

export const getPartsListForPandA = (partsWithSOSCode: QOItem[]) => {
  return partsWithSOSCode?.map((part) => {
    return {
      partNumber: part?.partNumber,
      quantity: part?.quantity,
      sosCode: part?.sosCode,
      referenceId: part?.referenceId
    };
  });
};

export const getPartsWithFlags = (
  priceAndAvailabilityData: PriceAvailabilityLegacyItem[],
  partsWithSOSCode: QOItem[]
) => {
  return partsWithSOSCode?.map((part) => {
    const partWithPriceData = priceAndAvailabilityData?.find(
      (item) => item?.referenceId === part?.referenceId
    );
    return {
      ...part,
      partNumber: partWithPriceData?.partNumber,
      isCatPart: partWithPriceData?.partLevelFlags?.isCatPart ?? true
    };
  });
};

export const mapPriceAndAvailabilityResponse = (
  headlessPAndAResponse: PriceAvailabilityResponseHeadless
) => {
  return {
    isGstIncludesTax: headlessPAndAResponse?.isGstIncludesTax ?? false,
    isGstAvailable: headlessPAndAResponse?.isGstAvailable ?? false,
    unitOfMeasure: headlessPAndAResponse?.unitOfMeasure ?? '',
    currencySymbol: headlessPAndAResponse?.currencySymbol ?? '',
    currency: headlessPAndAResponse?.currency ?? '',
    items: headlessPAndAResponse?.items?.map((lineItem, index) => {
      return {
        replacementMessage: lineItem?.replacementMessage ?? '',
        quantity: lineItem?.quantity ?? 1,
        remanParts: mapParts(lineItem?.remanParts),
        partLevelFlags: mapPartLevelFlags(lineItem?.partLevelFlags),
        weight: lineItem.weight ?? '',
        availability: mapAvailability(lineItem?.availability),
        partName: lineItem?.partName ?? '',
        referenceId: lineItem?.referenceId ?? '',
        hoseAssemblyIndicator: false, // not available in headless
        packageQty: lineItem?.packageError?.packageQuantity,
        sosCode: lineItem?.sosCode ?? '',
        itemId: lineItem?.itemId ?? '',
        sourcingDetails: !isEmpty(lineItem?.sourcingDetails)
          ? lineItem?.sourcingDetails
          : mapSourcingDetails(lineItem),
        partNumber: lineItem?.partNumber ?? '',
        id: lineItem.id ?? '',
        alternateParts: mapParts(lineItem?.alternateParts),
        replacementParts: mapParts(lineItem?.replacementParts, true),
        classicParts: mapParts(lineItem?.classicParts),
        price: lineItem?.price ?? ({} as Price),
        type: '', // not available in headless
        index: index,
        originalPart: '', // not available in headless
        errors: {
          errorMessage: '',
          errorCode: ''
        }, // not available in headless
        packageError: lineItem?.packageError
      };
    }),
    taxDisclaimerMessage: '', // not available in headless
    taxDisclaimerMessageFooter: '' // not available in headless
  } as PriceAvailabilityLegacyResponse;
};

const getCoreCharges = (sos) =>
  sos?.price?.formatted?.coreCharges?.includes('0.00')
    ? null
    : (sos?.price?.formatted?.coreCharges ?? null);

const getAvailability = (sos) =>
  sos?.availability?.map((a) => ({
    availabilityMessage: a?.message ?? '',
    quantityAvailable: a?.quantityAvailable ?? ''
  })) ?? [];

const mapSosItemList = (sosList: SOSListItemHeadless[]) => {
  return sosList?.map((sos) => ({
    unitPrice: sos?.price?.formatted?.unitPrice ?? '',
    totalPrice: sos?.price?.formatted?.totalPrice ?? '',
    sosPartNumber: sos?.sosPartNumber ?? '',
    sosPartName: sos?.sosPartName ?? '',
    sos: sos?.sosName ?? '',
    sosCode: sos?.sosCode ?? '',
    errors: sos?.errors ?? { errorMessage: '', errorCode: '' },
    coreCharges: getCoreCharges(sos),
    hoseAssemblyIndicator: false,
    availability: getAvailability(sos),
    sourcingDetails: {
      storeAvailability: {
        inStock: sos?.sourcingDetails?.storeAvailability?.inStock ?? null,
        byDate: sos?.sourcingDetails?.storeAvailability?.byDate ?? [],
        contactDealer:
          sos?.sourcingDetails?.storeAvailability?.contactDealer ?? null
      },
      availabilityByDate: sos?.sourcingDetails?.availabilityByDate ?? [],
      availabilityBySource: sos?.sourcingDetails?.availabilityBySource ?? []
    }
  }));
};

export const mapSosResponse = (
  sosItems: SourceOfSupplyResponseHeadless
): SourceOfSupplyResponse => {
  return {
    items: sosItems?.items?.map((item) => ({
      partNumber: item?.partNumber ?? '',
      requestedQuantity: item?.requestedQuantity ?? '',
      referenceId: item?.referenceId ?? '',
      currency: item?.currency ?? '', // not available in headless
      sosList: mapSosItemList(item?.sosList ?? [])
    })),
    taxDisclaimerMessage: '',
    taxDisclaimerMessageFooter: '',
    isGstIncludesTax: false,
    isGstAvailable: false
  };
};

const mapParts = (
  parts: Part[] | ReplacementPartsHeadless[],
  isReplacementPart?: boolean
) => {
  return parts?.map((part) => {
    return {
      replacementMessage: part?.replacementMessage ?? '',
      quantity: part?.quantity ?? 1,
      partLevelFlags: mapPartLevelFlags(part?.partLevelFlags),
      weight: part?.weight ?? '',
      availability: mapAvailability(part?.availability),
      partName: part?.partName ?? '',
      hoseAssemblyIndicator: false, // not available in headless,
      packageQty: isReplacementPart
        ? part?.packageError?.packageQuantity
        : part?.packageQty,
      sosCode: part?.sosCode ?? '',
      itemId: part?.itemId ?? '',
      sourcingDetails: mapSourcingDetails(part),
      price: part?.price ?? ({} as Price),
      partNumber: part?.partNumber ?? '',
      id: part?.id ?? ''
    };
  });
};

const mapPartLevelFlags = (flags: PartLevelFlags) => ({
  isReman: flags?.isRemanPart ?? false,
  isPartExistInCatalog: flags?.isExistingInCatalog ?? false,
  isAlternatePartsExist: flags?.isAlternatePartsExist ?? false,
  isCatPart: flags?.isCatPart ?? false,
  isDealerPartInCatalog: flags?.isDealerPartInCatalog ?? false,
  isIndirectReplacement: flags?.isIndirectReplacement ?? false,
  isAddToCartEnabled: flags?.isAddToCartEnabled ?? false,
  isNonReturnable: flags?.isNonReturnable ?? false
});

const mapAvailability = (availabilityList: AvailabilityHeadless[]) =>
  availabilityList.map(
    (
      availabilityItem = {
        availabilityMessage: '',
        quantityAvailable: '',
        showDealerContactInformation: false,
        availableByDate: ''
      }
    ) => ({
      ...availabilityItem,
      showContactDealerPopUp: availabilityItem?.showDealerContactInformation
    })
  );

const mapSourcingDetails = (lineItem: ProductPriceAvailabilityHeadless) => ({
  storeAvailability: {}, // not available in headless
  availabilityByDate: mapAvailability(lineItem?.availability),
  availabilityBySource:
    lineItem?.availabilityBySource?.map((availabilityItem) => ({
      ...availabilityItem,
      availStoreList: availabilityItem?.storesWithAvailability
    })) ?? []
});

export const getPartsWithSOS = (
  parts: AvailabilityBySourceItem[] | SOSItem[]
) => parts?.filter((item) => item.sosList?.length > 1);

export const getPartsWithReplacements = (
  items: PriceAvailabilityLegacyItem[]
) => items?.filter(({ replacementParts }) => replacementParts?.length > 0);

export const getPartsWithNoReplacements = (
  items: PriceAvailabilityLegacyItem[]
) => items?.filter(({ replacementParts }) => replacementParts?.length === 0);

export const getAlternatives = (
  items: PriceAvailabilityLegacyItem[]
): {
  partsWithAlternatives: PriceAvailabilityLegacyItem[];
  partsWithNoAlternatives: PriceAvailabilityLegacyItem[];
  partsAlternativeList: string[];
} =>
  items.reduce(
    (acc, item) => {
      if (
        item.remanParts.length > 0 ||
        item.classicParts.length > 0 ||
        item.alternateParts.length > 0
      ) {
        acc.partsWithAlternatives.push(item);
        [
          ...item.remanParts,
          ...item.classicParts,
          ...item.alternateParts
        ].forEach((x) => acc.partsAlternativeList.push(x.partNumber));
      } else {
        acc.partsWithNoAlternatives.push(item);
      }
      return acc;
    },
    {
      partsWithAlternatives: [],
      partsWithNoAlternatives: [],
      partsAlternativeList: []
    }
  );

export const getPartType = (
  item: PriceAvailabilityLegacyItem,
  products: Product[],
  isRemanPart = false
) => {
  const catalogPart = products?.find((a) => a.partNumber === item.partNumber);

  if (item.partLevelFlags.isCatPart && !isEmpty(catalogPart)) {
    return catalogPart?.alternatePartType || PART_TYPE.ALTERNATE;
  } else if (isRemanPart) {
    return PART_TYPE.DEALERREMAN;
  }
  return PART_TYPE.DEALERALTERNATE;
};

export const getCartCookies = (values: string[]) => {
  const cookieV = [];
  const cookies = [
    'zeroPriceString',
    'indirectReplacedString',
    'indirectReplacedParts',
    'invalidParts'
  ];

  cookies.forEach((cookie) => {
    const value = getSetCookieValue(values, cookie);
    if (value) {
      const parts = value?.trim().split('=')[1].split('~')[0].split('__');
      const valueArray = value.split('=');
      cookieV.push({ name: valueArray[0], value: valueArray[1], parts });
    }
  });

  return cookieV;
};

export const mapItemsToAdd = (
  parts: OrderItemsResponse[],
  itemNotes: LineItemNotes[]
): OrderItemsAddPartToSavedList[] => {
  // Create a lookup map for itemNotes by orderItemId
  const notesMap = new Map(itemNotes?.map((note) => [note.orderItemId, note]));

  return (
    parts?.map((item) => {
      const note = notesMap?.get(item.orderItemId);

      return {
        partNumber: item.partNumber,
        quantity: item.quantity,
        note: note?.partNote ?? '',
        serialNumber: note?.customerSerialNumber ?? '',
        customerItemNumber: note?.lineItemNumber ?? '',
        customerPartNumber: note?.customerPartNumber ?? ''
      };
    }) || []
  );
};

export const getUniqueItems = (items: QuickOrderCookieData[]) => {
  const itemNumberMap = new Map();
  const itemsReverse = [...items]?.reverse();
  for (const item of itemsReverse) {
    if (!itemNumberMap.has(item?.itemNumber) && !isEmpty(item?.itemNumber)) {
      itemNumberMap.set(item?.itemNumber, item);
    }
  }
  return (
    itemNumberMap &&
    [...itemNumberMap.values()]?.map(({ page, itemNumber }) => ({
      page,
      itemNumber
    }))
  );
};

export const getPartInQOData = (qoUniqueCookieData, partsToAdd) => {
  const pageValue = qoUniqueCookieData[0]?.page;
  partsToAdd?.forEach((item) => {
    if (
      !qoUniqueCookieData.some((entry) => entry.itemNumber === item.partNumber)
    ) {
      qoUniqueCookieData?.push({
        page: pageValue,
        itemNumber: item.partNumber
      });
    }
  });
  return qoUniqueCookieData;
};

/**
 * Utility Function to process the fluid attribute
 * @param fluidAttributes
 * @returns a map to look up fluid part number and sos code by dealer code
 */
const processFluidAttributes = (
  fluidAttributes?: string[]
): { [dealerCode: string]: { fluidPartNumber: string; sosCode: string } } => {
  if (!Array.isArray(fluidAttributes)) return null;
  return fluidAttributes.reduce((acc, item) => {
    const [dealerCode, fluidPartNumber, sosCode] = item.split(',');
    if (dealerCode && fluidPartNumber && sosCode) {
      return {
        ...acc,
        [dealerCode]: {
          fluidPartNumber,
          sosCode
        }
      };
    }
    return acc;
  }, {});
};

/**
 * Utility function to get fluid attributes for a dealer
 * @param productPriceData
 * @param dealerCode
 * @returns fluid part number and sos code for add to cart service or an empty object
 */
export const getFluidAttributeData = (
  fluidAttributes?: string[],
  dealerCode?: string
): { fluidPartNumber: string; sosCode: string } | Record<string, never> => {
  if (fluidAttributes && dealerCode) {
    const processedFluidAttributes = processFluidAttributes(fluidAttributes);
    if (processedFluidAttributes[dealerCode]) {
      return processedFluidAttributes[dealerCode];
    }
    return {};
  }
  return {};
};

/**
 * function to remove the sos code from a part number before adding to cart
 * @param partNumber
 * @param sosCode
 * @param isCatPart
 * @returns part number without sos code appended
 */
export const getPartNumberForAddToCart = (
  partNumber: string,
  sosCode: string,
  isCatPart: boolean
): string => {
  if (!sosCode || isCatPart) {
    return partNumber;
  }
  if (typeof partNumber !== 'string') {
    return '';
  }
  return partNumber.split(`-${sosCode}`)[0];
};

export const upsertItemNote = (prevNotes, newNote) => {
  const {
    orderItemId,
    customerSerialNumber,
    partNote,
    customerPartNumber,
    lineItemNumber
  } = newNote;
  const existingIndex = prevNotes?.findIndex(
    (note) => note.orderItemId === orderItemId
  );

  if (existingIndex !== -1) {
    // Update the existing entry
    const updatedNotes = [...prevNotes];
    updatedNotes[existingIndex] = {
      ...updatedNotes[existingIndex],
      customerSerialNumber,
      partNote,
      customerPartNumber,
      lineItemNumber
    };
    return updatedNotes;
  } else {
    // Add a new entry
    return [
      ...prevNotes,
      {
        orderItemId,
        customerSerialNumber,
        partNote,
        customerPartNumber,
        lineItemNumber
      }
    ];
  }
};
